import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import {
  useCampaignMutations,
  useFetchCampaignGroupAssignments,
  useFetchUserGroups,
} from '../../../../hooks/campaign-hooks';
import {
  Campaign,
  CAMPAIGN_GROUP_ACCESS_ADMIN,
  CAMPAIGN_GROUP_ACCESS_USER,
  CAMPAIGN_STATUSES,
  CAMPAIGN_THEMES,
  CampaignGroupAccess,
  CampaignStatus,
  CampaignTheme,
  HelpLink,
  campaignTheme,
  helpLinksOf,
  slugifyKey,
  uniqueKey,
} from '../../../../support/campaigns';
import {
  GhostButton,
  PrimaryButton,
  SectionHeader,
  builderStyles,
} from './primitives';
import { ReferenceMultiSelect, ReferenceOption } from './ReferenceSelect';
import AiFieldNote from './ai/AiFieldNote';
import { LinkListEditor } from './LinkListEditor';

/**
 * Campaign-record create/edit form for the Campaign Builder admin. Adapted 1:1 from the user-space
 * CampaignForm (same fields, same key/slug prefill, same create/edit payloads) but rendered inside
 * the scoped builder design system and with an AI field-note surface under the description.
 *
 * Writes are full-record: edit spreads the fetched campaign and overrides the edited fields (a delta
 * PUT 400s on the Go runtime); create posts the whole record with version:1 and empty JSON bags.
 */

interface CampaignFormValues {
  name: string;
  key: string;
  slug: string;
  description: string;
  status: CampaignStatus;
  owner: string;
  estimated_duration_minutes: string;
  image_url: string;
  is_active: boolean;
  /** Campaign-wide visual theme; persisted to `metadata.theme` ('' = default tokens). */
  theme: CampaignTheme | '';
  /** Campaign-level help/resource links; persisted to `metadata.helpLinks`. */
  helpLinks: HelpLink[];
}

const toValues = (campaign?: Campaign): CampaignFormValues => ({
  name: campaign?.name ?? '',
  key: campaign?.key ?? '',
  slug: campaign?.slug ?? '',
  description: campaign?.description ?? '',
  status: campaign?.status ?? 'draft',
  owner: campaign?.owner ?? '',
  estimated_duration_minutes:
    campaign?.estimated_duration_minutes != null ? String(campaign.estimated_duration_minutes) : '',
  image_url: campaign?.image_url ?? '',
  is_active: campaign?.is_active ?? true,
  theme: campaignTheme(campaign) ?? '',
  helpLinks: helpLinksOf(campaign),
});

const numOrNull = (v: string): number | null => {
  const t = (v || '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export function CampaignMetaForm(props: {
  /** The campaign being edited; omit for create mode. */
  campaign?: Campaign;
  /** Existing campaign keys, so a new key can be made unique. */
  takenKeys?: string[];
  /** When false, every field is disabled and save is hidden. */
  canWrite: boolean;
  onDone: () => void;
  onCreated?: (id: number) => void;
}): JSX.Element {
  const { campaign, takenKeys = [], canWrite, onDone, onCreated } = props;
  const isEdit = !!campaign;
  const { create, update, isSaving, error } = useCampaignMutations('campaign');

  // Group-linked access control (edit only — the join rows are keyed by the campaign's id, which a
  // create doesn't have yet). Two independent arrays: USER groups (who may see/run the campaign) and
  // ADMIN groups (who administer it), each persisted as `campaign-group-assignment` rows on save.
  const groupMutations = useCampaignMutations('groupAssignment');
  const { data: userGroups } = useFetchUserGroups({ enabled: isEdit });
  const { data: groupAssignments } = useFetchCampaignGroupAssignments(campaign?.id);

  const groupOptions: ReferenceOption[] = React.useMemo(
    () => (userGroups ?? []).map((g) => ({ value: String(g.id), label: g.name })),
    [userGroups]
  );

  const [userGroupIds, setUserGroupIds] = React.useState<string[]>([]);
  const [adminGroupIds, setAdminGroupIds] = React.useState<string[]>([]);

  // Seed the two selects from the fetched join rows once they land (and re-seed if they change).
  React.useEffect(() => {
    if (!groupAssignments) return;
    setUserGroupIds(
      groupAssignments
        .filter((a) => a.access === CAMPAIGN_GROUP_ACCESS_USER)
        .map((a) => String(a.user_group))
    );
    setAdminGroupIds(
      groupAssignments
        .filter((a) => a.access === CAMPAIGN_GROUP_ACCESS_ADMIN)
        .map((a) => String(a.user_group))
    );
  }, [groupAssignments]);

  /**
   * Bring the persisted join rows in line with the two selects: create a row for every group added to
   * a list, delete the row for every group removed from it. Runs after the campaign PUT succeeds; the
   * two access roles are reconciled independently so a group can appear in either, both, or neither.
   */
  const reconcileGroupAssignments = async () => {
    if (!campaign) return;
    const rows = groupAssignments ?? [];
    const lists: Array<{ ids: string[]; access: CampaignGroupAccess }> = [
      { ids: userGroupIds, access: CAMPAIGN_GROUP_ACCESS_USER },
      { ids: adminGroupIds, access: CAMPAIGN_GROUP_ACCESS_ADMIN },
    ];
    const writes: Promise<unknown>[] = [];
    for (const { ids, access } of lists) {
      const existing = rows.filter((r) => r.access === access);
      const existingIds = new Set(existing.map((r) => String(r.user_group)));
      const desiredIds = new Set(ids);
      ids
        .filter((id) => !existingIds.has(id))
        .forEach((id) =>
          writes.push(
            groupMutations.create.mutateAsync({
              campaign: campaign.id,
              user_group: Number(id),
              access,
              metadata: {},
            })
          )
        );
      existing
        .filter((r) => !desiredIds.has(String(r.user_group)))
        .forEach((r) => writes.push(groupMutations.remove.mutateAsync(r.id)));
    }
    await Promise.all(writes);
  };

  const { control, handleSubmit, setValue, getValues, watch } = useForm<CampaignFormValues>({
    defaultValues: toValues(campaign),
  });

  // In create mode, keep key/slug in step with the name until the user edits them by hand.
  const [keyTouched, setKeyTouched] = React.useState(false);
  const onNameBlur = () => {
    if (isEdit || keyTouched) return;
    const base = uniqueKey(getValues('name'), takenKeys);
    setValue('key', base);
    setValue('slug', base);
  };

  const onSubmit = (values: CampaignFormValues) => {
    // Fold theme + help links into metadata, preserving any other keys the campaign already carries.
    const existingMeta =
      campaign?.metadata && typeof campaign.metadata === 'object'
        ? { ...(campaign.metadata as Record<string, any>) }
        : {};
    const metadata: Record<string, any> = { ...existingMeta };
    if (values.theme) metadata.theme = values.theme;
    else delete metadata.theme;
    const links = (values.helpLinks ?? []).filter((l) => l && l.url.trim());
    if (links.length) metadata.helpLinks = links.map((l) => ({ label: l.label.trim() || l.url.trim(), url: l.url.trim() }));
    else delete metadata.helpLinks;

    const common = {
      name: values.name.trim(),
      description: values.description.trim() || null,
      status: values.status,
      owner: values.owner.trim() || null,
      estimated_duration_minutes: numOrNull(values.estimated_duration_minutes),
      image_url: values.image_url.trim() || null,
      is_active: values.is_active,
      metadata,
    };

    if (isEdit && campaign) {
      update.mutate(
        { ...campaign, ...common, id: campaign.id },
        {
          onSuccess: async () => {
            try {
              await reconcileGroupAssignments();
            } finally {
              onDone();
            }
          },
        }
      );
    } else {
      const key = (values.key.trim() || uniqueKey(values.name, takenKeys)).trim();
      create.mutate(
        {
          key,
          slug: values.slug.trim() || key,
          version: 1,
          audience_rules: {},
          available_from: null,
          available_to: null,
          ...common,
        },
        {
          onSuccess: (row: Campaign) => {
            if (onCreated) onCreated(row.id);
            onDone();
          },
        }
      );
    }
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title={isEdit ? 'Edit campaign' : 'New campaign'}
        subtitle={isEdit ? campaign?.name : 'Define the campaign record.'}
      />

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            {(!!error || !!groupMutations.error) && (
              <Alert severity="error">Could not save the campaign. Please try again.</Alert>
            )}

            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  onBlur={() => {
                    field.onBlur();
                    onNameBlur();
                  }}
                  label="Campaign name"
                  size="small"
                  required
                  disabled={!canWrite}
                  error={!!fieldState.error}
                  fullWidth
                />
              )}
            />

            {!isEdit && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Controller
                  name="key"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        setKeyTouched(true);
                        field.onChange(slugifyKey(e.target.value));
                      }}
                      label="Key"
                      size="small"
                      required
                      disabled={!canWrite}
                      error={!!fieldState.error}
                      fullWidth
                      helperText="Stable natural key"
                    />
                  )}
                />
                <Controller
                  name="slug"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => field.onChange(slugifyKey(e.target.value))}
                      label="Slug"
                      size="small"
                      disabled={!canWrite}
                      fullWidth
                    />
                  )}
                />
              </Stack>
            )}

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  size="small"
                  multiline
                  minRows={2}
                  disabled={!canWrite}
                  fullWidth
                />
              )}
            />

            {canWrite && (
              <AiFieldNote
                entity="campaign"
                field="description"
                context={{
                  name: watch('name'),
                  description: watch('description'),
                  status: watch('status'),
                }}
                onUse={(v) => setValue('description', v)}
              />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Status"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                  >
                    {CAMPAIGN_STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <Controller
                name="estimated_duration_minutes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Duration (min)"
                    type="number"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        disabled={!canWrite}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Active"
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                )}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="owner"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Owner (email)"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="image_url"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Image URL"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Controller
              name="theme"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Theme"
                  size="small"
                  disabled={!canWrite}
                  fullWidth
                  helperText="Retints every object in this campaign — chrome, the Frontier runner, and the report."
                >
                  <MenuItem value="">Default (Cyber Academy)</MenuItem>
                  {CAMPAIGN_THEMES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="helpLinks"
              control={control}
              render={({ field }) => (
                <LinkListEditor
                  value={field.value ?? []}
                  onChange={field.onChange}
                  disabled={!canWrite}
                  help="Shown across the whole campaign. Leave empty to show no help control."
                />
              )}
            />

            {isEdit && (
              <Stack spacing={1.5}>
                <SectionHeader
                  compact
                  title="Group access"
                  subtitle="Link user groups that may see/run this campaign and groups that administer it. Leave both empty to keep it open to everyone."
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <ReferenceMultiSelect
                    label="User groups (can see / run)"
                    value={userGroupIds}
                    options={groupOptions}
                    onChange={setUserGroupIds}
                    disabled={!canWrite}
                    helperText="Members of these groups may access and run the campaign."
                  />
                  <ReferenceMultiSelect
                    label="Admin groups (can administer)"
                    value={adminGroupIds}
                    options={groupOptions}
                    onChange={setAdminGroupIds}
                    disabled={!canWrite}
                    helperText="Members of these groups may administer the campaign."
                  />
                </Stack>
              </Stack>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <GhostButton onClick={onDone} disabled={isSaving || groupMutations.isSaving}>
                Cancel
              </GhostButton>
              {canWrite && (
                <PrimaryButton type="submit" disabled={isSaving || groupMutations.isSaving}>
                  {isEdit ? 'Save campaign' : 'Create campaign'}
                </PrimaryButton>
              )}
            </Stack>
          </Stack>
        </Box>
    </div>
  );
}

export default CampaignMetaForm;
